import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  referenceType: string | null;
  referenceId: string | null;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
  // Phase 1D.1 lifecycle timestamps
  deliveredAt?: string | null;
  openedAt?: string | null;
  tappedAt?: string | null;
  deepLinkDestination?: string | null;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unreadCount: number;
  meta: PaginatedResponse<unknown>["meta"];
}

// Phase 5 hardening — backend now wraps responses in `{success: true, data: ...}`.
// Lab's request method returns the whole body, so the page reads `data.data.X`.
export function listNotifications(params?: {
  unread?: "true" | "false";
  page?: number;
  limit?: number;
}) {
  return api.get<{ success: true; data: NotificationListResponse }>(
    "/notifications",
    params,
  );
}

export function getUnreadCount() {
  return api.get<{ success: true; data: { unreadCount: number } }>(
    "/notifications/unread-count",
  );
}

export function markRead(id: string) {
  return api.post<{ success: true }>(`/notifications/${id}/read`, {});
}

export function markAllRead() {
  return api.post<{ success: true }>("/notifications/read-all", {});
}

// ─── Phase 1D.1 lifecycle markers ──────────────────────────────────

export function markDelivered(id: string) {
  return api.post<{ success: true; firstTransition: boolean }>(
    `/notifications/${id}/delivered`,
    {},
  );
}

export function markOpened(id: string) {
  return api.post<{ success: true; firstTransition: boolean }>(
    `/notifications/${id}/opened`,
    {},
  );
}

export function markTapped(id: string, deepLinkDestination?: string) {
  return api.post<{ success: true; firstTransition: boolean }>(
    `/notifications/${id}/tapped`,
    deepLinkDestination ? { deepLinkDestination } : {},
  );
}

// ─── Phase 1D.1 funnel-event tester ────────────────────────────────

export type FunnelName =
  | "identity_board"
  | "event_attendance"
  | "institution_lifecycle"
  | "event_organizer_lifecycle";

export function postFunnelEvent(input: {
  funnelName: FunnelName;
  step: string;
  metadata?: Record<string, unknown>;
}) {
  return api.post<ApiResponse<{ id: string }>>("/analytics/funnel-event", input);
}

// ─── Devices: where a push goes ────────────────────────────────────

export type DevicePlatform = "ios" | "android" | "web";

/** Register a push token (an FCM registration token) for the signed-in member. */
export function registerDevice(token: string, platform: DevicePlatform) {
  return api.post<void>("/notifications/devices", { token, platform });
}

/** Drop a push token, by POST (the form generated mobile clients use; DELETE takes a body). */
export function deregisterDeviceByPost(token: string) {
  return api.post<void>("/notifications/devices/deregister", { token });
}
